import { AnyObject } from "final-form";
import { get } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useForm } from "react-final-form";
import { useDispatch } from "react-redux";
import { Col, Container, Row } from "reactstrap";
import SimpleDatePickerField from "src/components/Form/SimpleDatePickerFieldV2/SimpleDatePickerField";
import SimpleTimePickerField from "src/components/Form/SimpleTimePickerFieldV2/SimpleTimePrickerField";
import TextField from "src/components/Form/TextFieldV2/TextFieldV2";
import { IIOTemplate, IIOTemplateStep } from "src/store/models/template.model";
import { fetchTemplate, fetchTemplatesList } from "src/store/reducers/templates";
import { MessageSequencer } from "src/views/TemplateBuilder/MessageSequencer";
import * as S from "../../../constants/StringConstants";
import { COLUMNS_WITH_CARRIER } from "../Constants/columns";
type Props = {
  values: any;
  errors: AnyObject;
  isActiveCampaign: boolean;
};

interface SelectTemplateProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string;
  isRequired: boolean;
  options: Array<{
    key: any;
    value: string;
    disabled?: boolean;
  }>;
}

const SelectTemplate: React.FC<SelectTemplateProps> = props => {
  const { label, disabled, isRequired, options, error, ...rest } = props;

  return (
    <div
      className={
        disabled ? "single-select-field-v2-disabled" : error ? "single-select-field-v2-error" : "single-select-field-v2"
      }>
      <ul>
        <li>
          <label>{label}</label>
          {isRequired ? <label className="required">&nbsp;*</label> : ""}
        </li>
        <li>
          <select className="single-select-field-v2__field-input" disabled={disabled} {...rest}>
            {options.map(item => (
              <option key={item.value} value={item.value} disabled={item.disabled}>
                {item.key}
              </option>
            ))}
          </select>
        </li>
        {error && <span>{error}</span>}
      </ul>
    </div>
  );
};

export const parseEmails = (s: any): string[] => {
  return s.split(",").filter(x => !!x);
};

const templateFromJSON = (json: any): IIOTemplate => {
  const ret: IIOTemplate = { ...json };
  ret.steps = ret.steps.map((step: IIOTemplateStep) => {
    if (step.kind === "email") {
      return {
        ...step,
        to: parseEmails(step.to),
        from: parseEmails(step.from),
        cc: parseEmails(step.cc),
        bcc: parseEmails(step.bcc),
        replyTo: parseEmails(step.replyTo),
      };
    } else {
      return { ...step, to: parseEmails(step.to) };
    }
  });
  return ret;
};

export const Journey: React.FC<Props> = props => {
  const { values, errors, isActiveCampaign } = props;
  const dispatch = useDispatch();
  const form = useForm();
  const { change } = useForm();
  const [templateList, setTemplateList] = useState<IIOTemplate[]>([]);
  const [templateId, setTemplateId] = useState<number | null>();

  useEffect(() => {
    (async () => {
      const result: any = await dispatch(fetchTemplatesList());
      const list = get(result, "value.data.items");
      setTemplateList(list);
    })();
  }, []);

  const fetchTemplateDetails = async (id: number) => {
    const result: any = await dispatch(fetchTemplate(id.toString()));
    const obj = get(result, "value.data");
    change("templateSteps", templateFromJSON(obj).steps);
    setTemplateId(id);
  };

  const onAddStepClicked = () => {
    let temporaryStepId = Date.now().toString(36) + Math.random().toString(36).substring(2);
    form.mutators.push("templateSteps", {
      kind: "email",
      to: [],
      from: [],
      cc: [],
      bcc: [],
      replyTo: [],
      intervalDays: 1,
      stepOrder: values.templateSteps.length,
      newStepId: temporaryStepId,
    });
  };

  const onStepDeleted = id => {
    form.mutators.push("deletedSteps", id);
  };

  return (
    <Container fluid>
      <div className="io-box">
        <Row>
          <h1 className="form-title">{S.CS_JOURNEY}</h1>
        </Row>
        <Row>
          <Col sm="6">
            <SelectTemplate
              disabled={isActiveCampaign}
              isRequired
              name="templateIndex"
              value={values.templateIndex}
              label={S.TPL_EXISTING_TEMPLATE}
              placeholder={S.TPL_EXISTING_TEMPLATE}
              options={[
                {
                  key: S.TPL_NEW_TEMPLATE,
                  value: null,
                },
                ...templateList.map((template, index) => ({
                  key: template.templateName,
                  value: `${template.templateId}`,
                })),
              ]}
              onChange={e => {
                const value = parseInt(e.target.value);
                form.change("templateIndex", value);
                const hasContent = values.templateSteps?.length > 0;
                if (hasContent && !confirm(S.TPL_CONFIRM_OVERWRITE)) {
                  return; // skip if we don't confirm
                }
                fetchTemplateDetails(value);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col sm="2">
            <SimpleTimePickerField name="options.time" label={S.CS_OPTIONS_DELIVERY_TIME} disabled={isActiveCampaign} />
          </Col>
          <Col sm="2">
            <SimpleDatePickerField
              name="options.firstDeliveryDate"
              label={S.CS_OPTIONS_FIRST_DELIVERY_DATE}
              disabled={isActiveCampaign}
              isFormMode
            />
          </Col>
          <Col sm="2">
            <SimpleDatePickerField name="options.expirationDate" label={S.CS_OPTIONS_EXPIRATION_DATE} isFormMode />
          </Col>
        </Row>
        <Row>
          <Col sm="6">
            <TextField
              name="options.emailFooter"
              label={S.CS_OPTIONS_EMAIL_FOOTER}
              disabled={isActiveCampaign}
              multiline
            />
          </Col>
        </Row>
      </div>
      <MessageSequencer
        key={templateId}
        name="templateSteps"
        values={values}
        enableCalendar={true}
        firstDate={moment(values.options.firstDeliveryDate).toDate()}
        columns={COLUMNS_WITH_CARRIER}
        onAddClicked={onAddStepClicked}
        onDeleteStep={onStepDeleted}
      />
    </Container>
  );
};
